"use client";

import { useEffect, useState } from "react";
import { getStartOfWeek } from "../dates";

/** Milliseconds from `now` to the following local Sunday at midnight. */
export function millisecondsUntilNextLocalWeek(now: Date): number {
  const nextWeekStart = getStartOfWeek(now);
  nextWeekStart.setDate(nextWeekStart.getDate() + 7);
  return Math.max(1, nextWeekStart.getTime() - now.getTime());
}

/**
 * Local Sunday-Saturday identity for a long-lived client view. The timeout is
 * deliberately armed from the current clock on every boundary so daylight
 * saving transitions do not turn a local calendar week into fixed 168-hour
 * intervals.
 */
export function useCurrentWeekStart(): Date {
  const [weekStart, setWeekStart] = useState(() => getStartOfWeek(new Date()));

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;

    const armNextBoundary = () => {
      timeout = setTimeout(() => {
        setWeekStart(getStartOfWeek(new Date()));
        armNextBoundary();
      }, millisecondsUntilNextLocalWeek(new Date()));
    };

    armNextBoundary();
    return () => clearTimeout(timeout);
  }, []);

  return weekStart;
}
